import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PaymentList from '../components/payments/PaymentList.tsx';
import PaymentForm from '../components/payments/PaymentForm.tsx';
import SettlementsTab from '../components/payments/Settlements.tsx';
import { usePayments } from '../hooks/usePayments.ts';
import '../styles/Payments.css';
import LoadingSpinner from '../components/shared/LoadingSpinner.tsx';
import ErrorMessage from '../components/shared/ErrorMessage.tsx';

interface PaymentFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  onRefetch: () => Promise<void>;
  initialData: {
    tenantId?: string;
    billId?: string;
    amount?: number;
  } | null;
}

const Payments: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'payments' | 'settlements'>(
    location.pathname === '/settlements' ? 'settlements' : 'payments'
  );
  const [isFormOpen, setIsFormOpen] = useState(() => {
    // Initialize form state based on navigation state
    return location.state?.openPaymentForm || false;
  });
  const [initialFormData, setInitialFormData] = useState(() => {
    return location.state?.openPaymentForm ? {
      tenantId: location.state.selectedTenantId,
      billId: location.state.selectedBillId,
      amount: location.state.amount
    } : null;
  });
  const { payments, loading, error, addPayment, refetchPayments, isRefetching } = usePayments();

  const handleInitialRouting = useCallback(() => {
    if (location.pathname === '/payments' && location.state?.initialData) {
      setIsFormOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    handleInitialRouting();
  }, [handleInitialRouting]);

  useEffect(() => {
    if (location.state?.openPaymentForm) {
      setIsFormOpen(true);
      // Clear the navigation state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  // Handle tab changes and URL updates
  const handleTabChange = (tab: 'payments' | 'settlements') => {
    setActiveTab(tab);
    navigate(tab === 'settlements' ? '/settlements' : '/payments');
  };

  // Sync URL with tab state
  useEffect(() => {
    const path = location.pathname;
    if (path === '/settlements' && activeTab !== 'settlements') {
      setActiveTab('settlements');
    } else if (path === '/payments' && activeTab !== 'payments') {
      setActiveTab('payments');
    }
  }, [location.pathname]);

  return (
    <div className="payments-page">
      {isRefetching && (
        <LoadingSpinner />
      )}
      <div className="header-actions">
        <div className="tabs-container">
          <button 
            className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={async () => {handleTabChange('payments'); await refetchPayments();}}
          >
            Payments
          </button>
          <button 
            className={`tab-button ${activeTab === 'settlements' ? 'active' : ''}`}
            onClick={() => handleTabChange('settlements')}
          >
            Settlements
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <div className="tab-content">
          {activeTab === 'payments' ? (
            <>
            {activeTab === 'payments' && (
              <div className="payments-header">
              <h2>Payments</h2>
              <button className="btn-primary" onClick={() => setIsFormOpen(true)}>
                + Record Payment
              </button>
            </div>
            )}
              <PaymentList 
                payments={payments} 
                onDelete={async () => {}} // Add empty onDelete handler
              />
              {isFormOpen && (
                <PaymentForm 
                  onClose={() => {
                    setIsFormOpen(false);
                  }}
                  onRefetch={async () => {
                    await refetchPayments();
                    setIsFormOpen(false);
                  }}
                  initialData={initialFormData}
                />
              )}
            </>
          ) : (
            <SettlementsTab />
          )}
        </div>
      )}
    </div>
  );
};

export default Payments;
